import React from 'react';
import { Calendar, User, ThumbsUp, MessageCircle, Tag, Edit2, Trash2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { BlogPost, Theme } from '../types';

interface BlogListProps {
  posts: BlogPost[];
  theme: Theme;
  onEdit?: (post: BlogPost) => void;
  onDelete?: (postId: string) => void;
  onLike?: (postId: string) => void;
}

export default function BlogList({ posts, theme, onEdit, onDelete, onLike }: BlogListProps) {
  return (
    <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-2">
      {posts.map(post => (
        <motion.article
          key={post.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`rounded-lg overflow-hidden shadow-lg ${
            theme === 'dark' ? 'bg-gray-800' : 'bg-white'
          }`}
        >
          {post.imageUrl && (
            <img
              src={post.imageUrl}
              alt={post.title}
              className="w-full h-64 object-cover"
            />
          )}
          
          <div className="p-6">
            <div className="flex flex-wrap gap-2 mb-4">
              {post.tags.map(tag => (
                <span
                  key={tag}
                  className={`px-3 py-1 rounded-full text-sm ${
                    theme === 'dark'
                      ? 'bg-gray-700 text-purple-300'
                      : 'bg-purple-100 text-purple-700'
                  }`}
                >
                  <Tag size={14} className="inline mr-1" />
                  {tag}
                </span>
              ))}
            </div>

            <Link to={`/blog/${post.id}`}>
              <h2 className={`text-2xl font-bold mb-3 hover:text-purple-500 transition-colors ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                {post.title}
              </h2>
            </Link>

            <div className="flex items-center gap-4 mb-4 text-sm">
              <div className={`flex items-center gap-1 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                <User size={16} />
                {post.author}
              </div>
              <div className={`flex items-center gap-1 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                <Calendar size={16} />
                {new Date(post.date).toLocaleDateString()}
              </div>
            </div>

            <p className={`mb-6 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
            }`}>
              {post.excerpt}
            </p>

            {post.relatedWords && post.relatedWords.length > 0 && (
              <div className="mb-6">
                <h3 className={`text-sm font-semibold mb-2 ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Related Words:
                </h3>
                <div className="flex flex-wrap gap-2">
                  {post.relatedWords.map(word => (
                    <span
                      key={word}
                      className={`px-2 py-1 rounded text-sm ${
                        theme === 'dark'
                          ? 'bg-purple-900 text-purple-100'
                          : 'bg-purple-50 text-purple-800'
                      }`}
                    >
                      {word}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-4 pt-4 border-t border-gray-200">
              <button
                onClick={() => onLike?.(post.id)}
                className={`flex items-center gap-1 ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                } hover:text-purple-600`}
              >
                <ThumbsUp size={16} />
                {post.likes}
              </button>
              <div className={`flex items-center gap-1 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                <MessageCircle size={16} />
                {post.comments.length}
              </div>
              <div className="ml-auto flex gap-2">
                {onEdit && (
                  <button
                    onClick={() => onEdit(post)}
                    className={`p-2 rounded-full ${
                      theme === 'dark'
                        ? 'hover:bg-gray-700'
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    <Edit2 size={16} />
                  </button>
                )}
                {onDelete && (
                  <button
                    onClick={() => onDelete(post.id)}
                    className={`p-2 rounded-full ${
                      theme === 'dark'
                        ? 'hover:bg-gray-700'
                        : 'hover:bg-gray-100'
                    } text-red-500`}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </motion.article>
      ))}

      {posts.length === 0 && (
        <div className="col-span-full text-center py-12">
          <p className={`text-lg ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>
            No blog posts yet. Be the first to create one!
          </p>
        </div>
      )}
    </div>
  );
}